import {
  useMediaQuery,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  FormControl,
  Button,
  Grid,
  Pagination,
  Fab,
  styled,
  Alert,
  AlertTitle,
} from "@mui/material";
import { useState, useRef, FormEvent, useEffect } from "react";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LayoutWithIcons from "./LayoutWithIcons";
import CommonModal from "./HWSubmitModal";
import { useCharactersContext } from "../hooks/Context";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

// interface LayoutProps {
//   Questions: {
//     question: string;
//     mediaUrl: string;
//     optionsList: { optionId: number; option: string; correctAnswer: string }[];
//   }[];
// }

// interface LayoutProps {
//   Questions: {
//     question: string;
//     mediaUrl: string;
//     optionsList: { optionId: number; option: string; correctAnswer: string }[];
//   }[];
// }

interface LayoutProps {
  Questions: Question[];
  sectionName: string;
  isGrammarLayout?: boolean;
  currentQuestionIndexProp?: number; // Add this to receive from props
}

const Layout9: React.FC<LayoutProps> = ({
  Questions,
  sectionName,
  isGrammarLayout = false,
  currentQuestionIndexProp = 0,
}) => {
  //console.log("Layout9 Questions: ", Questions);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    isGrammarLayout ? currentQuestionIndexProp : 0
  );
  const currentQuestion = Questions[currentQuestionIndex];
  const [modalOpen, setModalOpen] = useState(false);
  const [isCorrectSubmission, setIsCorrectSubmission] = useState<
    boolean | null
  >(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  useEffect(() => {
    if (isGrammarLayout && currentQuestionIndexProp !== undefined) {
      setCurrentQuestionIndex(currentQuestionIndexProp);
    }
  }, [isGrammarLayout, currentQuestionIndexProp]);

  // const options = [
  //   { id: "Option 1", label: "collecting sticks" },
  //   { id: "Option 2", label: "driving sticks" },
  //   { id: "Option 3", label: "playing cricket" },
  // ];
  // const correctOptions = ["Option 1"];
  const options = currentQuestion.optionsList.map((option) => ({
    id: `${option.option}`,
    label: option.option,
  }));

  const correctOptions = currentQuestion.optionsList
    .filter((option) => option.correctAnswer)
    .map((option) => `${option.option}`);

  //console.log("correctOptions ", correctOptions);

  // const question = "What was Sachin doing in the forest?";
  const questionParts = currentQuestion?.question?.split("<br>");
  const question = questionParts[1]?.trim() || "";
  const questionTypography = questionParts[0]?.trim() || "";
  const additionalTypography = questionParts[2] ? questionParts[2].trim() : "";

  const [selectedOptions, setSelectedOptions] = useState<string[][]>(() => {
    const storageKey = `selectedOptionsLayout9_${currentQuestion.questionPaperId}`;
    const savedOptions = localStorage.getItem(storageKey);
    return savedOptions
      ? JSON.parse(savedOptions)
      : Array.from({ length: Questions.length }, () => []);
  });

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const handleOptionChange = (option: string) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = [...prevSelectedOptions];
      const currentOptions = updatedOptions[currentQuestionIndex] || [];
      const isMultiSelect = correctOptions.length > 1;

      if (isMultiSelect) {
        if (currentOptions.includes(option)) {
          updatedOptions[currentQuestionIndex] = currentOptions.filter(
            (item) => item !== option
          );
        } else {
          updatedOptions[currentQuestionIndex] = [...currentOptions, option];
        }
      } else {
        // Single-selection radio button mode
        if (currentOptions.includes(option)) {
          updatedOptions[currentQuestionIndex] = [];
        } else {
          updatedOptions[currentQuestionIndex] = [option];
        }
      }

      return updatedOptions;
    });
  };

  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();
  const questionPaperId = currentQuestion.questionPaperId;

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAlert(true);
    // console.log("Selected options:", selectedOptions);

    const selectedForCurrentQuestion = selectedOptions[currentQuestionIndex];
    const isCorrect = correctOptions.every((option) =>
      selectedForCurrentQuestion.includes(option)
    );
    setIsCorrectSubmission(isCorrect);

    if (isCorrect) {
      // playCorrectSound();
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      if (questionIndex !== -1) {
        // Update the existing score
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      } else {
        // Add a new score for the question
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      const correctSelections = selectedOptions[currentQuestionIndex].filter(
        (option) => correctOptions.includes(option)
      );

      if (correctSelections.length > 0) {
        // playInCorrectSound();
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: 0,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      } else {
        // playInCorrectSound();
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: 0,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    }

    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctOptions.join(","),
      enteredOptionValue: selectedOptions[currentQuestionIndex].join(","),
    };

    // Reset selectedOptions for the next question
    //setSelectedOptions([]);

    // Check if there is already an answer for the current question
    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      // If an answer already exists, update the existing one
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      // If there is no existing answer, add the new answer
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }

    // console.log("updatedExamAnswer ", updatedExamAnswer);
    // console.log("examAnswers  ", examAnswers);
  };

  const handleAudioToggle = () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsAudioPlaying(!isAudioPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
  };

  const handleNavigate = (direction: "previous" | "next") => {
    if (direction === "previous") {
      setCurrentQuestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (direction === "next") {
      setCurrentQuestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, Questions.length - 1)
      );
    }
  };

  const handlePaginationChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentQuestionIndex(value - 1);
  };

  const playCorrectSound = () => {
    const correctSound = new Audio(
      require("../assets/correct-choice-43861.mp3")
    );
    correctSound.play();
    handleOpen();
  };

  const playInCorrectSound = () => {
    const incorrectSound = new Audio(
      require("../assets/wronganswer-37702.mp3")
    );
    incorrectSound.play();
    handleOpen();
  };

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const handleContinue = () => {
    setCurrentQuestionIndex((prevIndex) =>
      Math.min(prevIndex + 1, Questions.length - 1)
    );
    setModalOpen(false);
  };

  // const handleRetry = () => {
  //   setSelectedOptions([]);
  //   setModalOpen(false);
  // };

  const handleRetry = () => {
    setSelectedOptions([]);
    setModalOpen(false);

    if (retryCount < 1) {
      setRetryCount(retryCount + 1);
    } else {
      setCurrentQuestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, Questions.length - 1)
      );
      setRetryCount(0);
    }
  };

  const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
    paddingBottom: "0px",
    "&.Mui-checked": {
      color: theme.palette.secondary.main,
    },
  }));

  useEffect(() => {
    const storageKey = `selectedOptionsLayout9_${currentQuestion.questionPaperId}`;
    localStorage.setItem(storageKey, JSON.stringify(selectedOptions));
  }, [selectedOptions]);

  useEffect(() => {
    // When isViewMode is true, set selectedOptions to the correctOptions
    if (isViewMode) {
      const newSelectedOptions = Array(Questions.length).fill(correctOptions);
      setSelectedOptions(newSelectedOptions);
      // console.log("correctOptions:", correctOptions);
    }
  }, [currentQuestion]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: isViewMode
          ? "3%"
          : isMobile
          ? "3%"
          : isTablet
          ? "20%"
          : "auto",
      }}
    >
      {/* <CommonModal
        open={modalOpen}
        isCorrectSubmission={isCorrectSubmission}
        handleContinue={handleContinue}
        handleRetry={handleRetry}
        handleClose={handleClose}
      /> */}
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {isGrammarLayout ? (
        <>
          {currentQuestion && (
            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                pointerEvents: isViewMode ? "none" : "auto",
                maxWidth: isMobile ? "95vw" : "800px",
                width: "100%",
                mx: "auto",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    paddingBottom: "0px",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "700",
                      fontSize: isMobile ? "100%" : "110%",
                      textAlign: "center",
                    }}
                  >
                    {questionTypography}
                  </Typography>
                  {currentQuestion.mediaUrl && (
                    <img
                      src={currentQuestion.mediaUrl}
                      alt="image"
                      width="100"
                      height="100"
                    />
                  )}
                  <Fab
                    variant="extended"
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "12px" : "15px",
                      cursor: "default",
                      fontFamily: '"Nunito","Segoe UI", sans-serif',
                      backgroundColor: "#E12027 !important",
                      color: "white",
                      p: 1.5,
                      m: 1,
                      boxShadow: "none",
                      height: "auto",
                      borderRadius: "16px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      textAlign: "center",
                      maxWidth: isMobile ? "90vw" : "700px",
                      lineHeight: 1.4,
                    }}
                  >
                    {question}
                  </Fab>

                  {additionalTypography && (
                    <Typography
                      sx={{
                        fontWeight: "450",
                        fontSize: isMobile ? "100%" : "110%",
                        margin: "1vh",
                      }}
                    >
                      {additionalTypography}
                    </Typography>
                  )}
                </Box>

                <form onSubmit={handleFormSubmit}>
                  {options.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 0,
                        }}
                      >
                        <FormControl>
                          <Box
                            sx={{
                              display: "flex",
                              ...(isMobile && {
                                flexDirection: "column",
                              }),
                              marginLeft: "auto",
                              marginRight: "auto",
                            }}
                          >
                            {options.map((option, index) => (
                              <FormControlLabel
                                key={option.id}
                                control={
                                  <CustomCheckbox
                                    size="small"
                                    sx={{
                                      paddingBottom: "0px",
                                      color: "secondary.main",
                                      "&:checked": {
                                        color: "secondary.main",
                                      },
                                      "&.Mui-checked": {
                                        color: "secondary.main !important",
                                      },
                                    }}
                                    checked={selectedOptions[
                                      currentQuestionIndex
                                    ].includes(option.id)}
                                    onChange={() => handleOptionChange(option.id)}
                                    // disabled={
                                    //   selectedOptions.length ===
                                    //     maxSelectableCheckboxes &&
                                    //   !selectedOptions.includes(option.id)
                                    // }
                                  />
                                }
                                label={option.label}
                              />
                            ))}
                          </Box>
                        </FormControl>
                      </Box>
                      {!isViewMode && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                          }}
                        >
                          <Button type="submit" variant="contained">
                            Submit
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <LayoutWithIcons onNavigate={handleNavigate}>
          {currentQuestion && (
            <Card
              variant="outlined"
              sx={{
                borderRadius: "12px",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                pointerEvents: isViewMode ? "none" : "auto",
                maxWidth: isMobile ? "95vw" : "800px",
                width: "100%",
                mx: "auto",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    paddingBottom: "0px",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "700",
                      fontSize: isMobile ? "100%" : "110%",
                      textAlign: "center",
                    }}
                  >
                    {questionTypography}
                  </Typography>
                  {/* <audio
                  ref={audioRef}
                  src="https://ccrma.stanford.edu/~jos/mp3/bachfugue.mp3"
                  onEnded={handleAudioEnded}
                ></audio>
                <IconButton
                  onClick={handleAudioToggle}
                  title="Click to play audio"
                  sx={{
                    // paddingBottom: "0px !important",
                    fontSize: "33px !important",
                    color: "#E12027 !important",
                    backgroundColor: "transparent !important",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                    marginTop: "1px !important",
                  }}
                >
                  {isAudioPlaying ? (
                    <PauseCircleRoundedIcon fontSize="inherit" />
                  ) : (
                    <VolumeUpRoundedIcon fontSize="inherit" />
                  )}
                </IconButton> */}
                  {currentQuestion.mediaUrl && (
                    <img
                      src={currentQuestion.mediaUrl}
                      alt="Plant"
                      width="100"
                      height="100"
                    />
                  )}
                  <Fab
                    variant="extended"
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "12px" : "15px",
                      cursor: "default",
                      fontFamily: '"Nunito","Segoe UI", sans-serif',
                      backgroundColor: "#E12027 !important",
                      color: "white",
                      p: 1.5,
                      m: 1,
                      boxShadow: "none",
                      height: "auto",
                      borderRadius: "16px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      textAlign: "center",
                      maxWidth: isMobile ? "90vw" : "700px",
                      lineHeight: 1.4,
                    }}
                  >
                    {question}
                  </Fab>

                  <Typography
                    sx={{
                      fontWeight: "450",
                      fontSize: isMobile ? "100%" : "110%",
                      margin: "1vh",
                    }}
                  >
                    {additionalTypography}
                  </Typography>
                </Box>

                <form onSubmit={handleFormSubmit}>
                  {options.length > 0 && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 0,
                        }}
                      >
                        <FormControl>
                          <Box
                            sx={{
                              display: "flex",
                              ...(isMobile && {
                                flexDirection: "column",
                              }),
                              marginLeft: "auto",
                              marginRight: "auto",
                            }}
                          >
                            {options.map((option, index) => (
                              <FormControlLabel
                                key={option.id}
                                control={
                                  <CustomCheckbox
                                    size="small"
                                    sx={{
                                      paddingBottom: "0px",
                                      color: "secondary.main",
                                      "&:checked": {
                                        color: "secondary.main",
                                      },
                                      "&.Mui-checked": {
                                        color: "secondary.main !important",
                                      },
                                    }}
                                    checked={selectedOptions[
                                      currentQuestionIndex
                                    ].includes(option.id)}
                                    onChange={() => handleOptionChange(option.id)}
                                    // disabled={
                                    //   selectedOptions.length ===
                                    //     maxSelectableCheckboxes &&
                                    //   !selectedOptions.includes(option.id)
                                    // }
                                  />
                                }
                                label={option.label}
                              />
                            ))}
                          </Box>
                        </FormControl>
                      </Box>
                      {!isViewMode && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 2,
                          }}
                        >
                          <Button type="submit" variant="contained">
                            Submit
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          )}
          <Grid
            container
            lg={12}
            md={12}
            sm={12}
            xs={12}
            justifyContent="center"
          >
            <Pagination
              count={Questions.length}
              shape="rounded"
              sx={{
                marginBottom: "1vh",
                paddingTop: "4%",
              }}
              page={currentQuestionIndex + 1}
              onChange={handlePaginationChange}
            />
          </Grid>
        </LayoutWithIcons>
      )}
    </div>
  );
};

export default Layout9;
